/**
 * jr-account.js — the Jeremy's Razors customer account UI.
 *
 * ONE renderer, TWO hosts. It draws the whole account — subscriptions and their
 * actions, loyalty, payment method, shipping addresses, buy-it-again, reviews —
 * into whatever element you hand it:
 *
 *   templates/page.account.liquid   /pages/my-account, the full-width page
 *   snippets/jr-account-drawer.liquid   the slide-out off the header icon
 *
 * It lived inline in the page template until the drawer needed to manage
 * everything too (Jon, 2026-07-31). Two copies of ~800 lines of rendering and
 * mutation logic would have drifted within a week, and the subtle parts — the
 * HTTP-200-{ok:false} error contract, the optimistic-update rules, the IDOR
 * assumptions — are exactly the parts that must not.
 *
 * Everything is driven by /apps/jr/ (Shopify App Proxy -> the jr-account edge
 * function), which verifies identity from the signed proxy request and performs
 * subscription and address mutations server-side against Recharge. There is no
 * client SDK and no credential in the theme.
 *
 * MOUNTING TWICE ON ONE PAGE IS SUPPORTED and is why every DOM lookup in here
 * is scoped to the mount root rather than to `document`: both hosts render the
 * same markup and the same ids, so a document-level query would reach into the
 * other mount. (In practice the account page suppresses the drawer, but the
 * scoping is what makes that a choice rather than a requirement.)
 *
 *   JRAccount.mount(rootEl, {
 *     loginUrl:     where the signed-out CTA goes
 *     compact:      true inside the drawer — tighter grids, no page chrome
 *     scrollToHash: page only; jump to #payment etc. once sections exist
 *     focus:        page only; 'payment' puts Payment method first (see render)
 *   });
 */
window.JRAccount = (function () {
  function mount(root, opts) {

    if (!root) return;
    opts = opts || {};
    var LOGIN_URL = opts.loginUrl || '/account/login';
    // Shopify's own route (`routes.account_logout_url`), passed in by the host
    // because this file is a static asset and can't render Liquid. It ends the
    // customer session and drops them on the homepage. A plain GET link is what
    // Shopify's themes use; there is no POST form to submit.
    var LOGOUT_URL = opts.logoutUrl || '/account/logout';
    // Rendered by Liquid and passed in, because this file is a static asset and
    // cannot resolve `asset_url`. Without it the cancellation flow's rail has
    // no background-image and paints a flat navy block — which is exactly what
    // shipped on 2026-08-30, on desktop as well as mobile.
    var PORTRAIT_URL = opts.portraitUrl || '';
    var SHOP_URL = opts.shopUrl || '/collections/all';
    var HOSTED_PORTAL = '/tools/recurring/login';
    // The card screen, reachable WITHOUT a token (Jon, 2026-08-05).
    //
    // Recharge's own portal does this: request any section while unauthenticated
    // and it 302s to `/tools/recurring/login?redirect=customer_portal/<section>`,
    // carrying the destination through the login. So we can name the card screen
    // as the destination and let Recharge handle authentication — a customer who
    // already has a Recharge session lands straight on it, and one who doesn't
    // enters their email once and still lands on it.
    //
    // This exists because the token route is broken: jr-account asks Recharge for
    // `customers/{id}/customer_portal_access`, which is NOT an endpoint in the
    // 2021-11 Customers API, so the call always fails and `payment_url` and
    // `portal_url` both come back null (confirmed live). We then fell back to the
    // bare portal login, which for a customer holding a Recharge session cookie
    // bounces to the portal OVERVIEW — the exact screen the deep link was meant
    // to skip. The backend fix is tracked separately in the jbs repo; this makes
    // the theme correct on its own, with no token and no round trip.
    var PORTAL_CARD_URL = '/tools/recurring/login?redirect=' + encodeURIComponent('customer_portal/payment_methods');
    // Every lookup below is scoped to `root`, never to `document`. Two mounts
    // can be alive at once and they render identical ids/markup, so a
    // document-level query would reach into the other one.
    function el(sel) { return root.querySelector(sel); }

    function esc(s) { var d = document.createElement('div'); d.textContent = (s == null ? '' : String(s)); return d.innerHTML; }
    function money(cents) { return cents == null ? '' : '$' + (cents / 100).toFixed(2); }
    function fmtDate(s) {
      if (!s) return '';
      try { return new Date(s + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); }
      catch (e) { return s; }
    }
    function today() { return new Date().toISOString().slice(0, 10); }
    function addInterval(iso, intervalStr) {
      var d = new Date((iso || today()) + 'T00:00:00');
      var parts = String(intervalStr || '1 month').trim().split(/\s+/);
      var n = parseInt(parts[0], 10) || 1;
      var unit = (parts[1] || 'month').toLowerCase();
      if (unit.indexOf('month') === 0) d.setMonth(d.getMonth() + n);
      else if (unit.indexOf('week') === 0) d.setDate(d.getDate() + n * 7);
      else d.setDate(d.getDate() + n);
      return d.toISOString().slice(0, 10);
    }

    // ---- Subscription actions via our backend (App Proxy → jr-account) --------
    // POST the action to /apps/jr/; the edge function verifies identity (App
    // Proxy signature) + ownership, then calls the Recharge Admin API server-side.
    // No client SDK — that path can't load reliably in a no-build theme.
    function callAction(payload) {
      return fetch('/apps/jr/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(payload)
      }).then(function (r) {
        return r.json().catch(function () { return {}; }).then(function (j) {
          if (!r.ok || !j || !j.ok) throw new Error((j && j.error) || ('http ' + r.status));
          return j;
        });
      });
    }
    // run(btn, payload, onOk): busy-state + optimistic onOk(result) + fallback.
    function run(btn, payload, onOk) {
      var orig = btn ? btn.textContent : '';
      if (btn) { btn.disabled = true; btn.textContent = 'Working…'; }
      return callAction(payload)
        .then(function (res) { if (onOk) onOk(res); })
        .catch(function (err) {
          try { console.warn('jr-account action failed', err); } catch (e) {}
          if (window.confirm('We couldn’t update that just now. Open the full subscription portal instead?')) window.location.href = HOSTED_PORTAL;
        })
        .then(function () { if (btn) { btn.disabled = false; btn.textContent = orig; } });
    }

    // ---- Optimistic UI helpers ----------------------------------------------
    function card(subId) { return root.querySelector('[data-sub-card="' + subId + '"]'); }
    function setNext(subId, iso) {
      var c = card(subId); if (!c) return;
      var whenEl = c.querySelector('[data-next-el]'); if (whenEl) whenEl.textContent = fmtDate(iso);
      c.querySelectorAll('[data-next]').forEach(function (b) { b.setAttribute('data-next', iso); });
    }
    // Keep the "Every N months" line and the buttons' data-interval in step
    // after a cadence change, so a follow-up Skip pushes by the NEW interval.
    function setInterval_(subId, interval) {
      var c = card(subId); if (!c) return;
      var intervalEl = c.querySelector('[data-interval-el]');
      if (intervalEl) intervalEl.textContent = 'Every ' + interval;
      c.querySelectorAll('[data-interval]').forEach(function (b) { b.setAttribute('data-interval', interval); });
    }
    function note(subId, msg) { var c = card(subId); if (!c) return; var n = c.querySelector('[data-note]'); if (n) { n.textContent = msg; n.hidden = false; } }
    function closePanel(subId) { var c = card(subId); if (!c) return; var p = c.querySelector('[data-panel]'); if (p) { p.hidden = true; p.innerHTML = ''; } }
    function markCancelled(subId) {
      var c = card(subId); if (!c) return;
      c.classList.add('jr-sub--cancelled');
      var a = c.querySelector('.jr-sub__actions'); if (a) a.remove();
      closePanel(subId);
      note(subId, '✓ Subscription cancelled. You can restart anytime.');
    }
    function openPanel(subId, html) { var c = card(subId); if (!c) return; var p = c.querySelector('[data-panel]'); if (p) { p.innerHTML = html; p.hidden = false; } }

    function reschedulePanel(subId, nextIso) {
      return '<div class="jr-panel"><label>New next-shipment date'
        + '<input type="date" min="' + today() + '" value="' + esc(nextIso || '') + '"></label>'
        + '<div class="jr-panel__actions">'
        + '<button type="button" class="jr-act jr-act--primary" data-jr="reschedule-save" data-sub="' + esc(subId) + '">Save date</button>'
        + '<button type="button" class="jr-act" data-jr="panel-close" data-sub="' + esc(subId) + '">Never mind</button>'
        + '</div></div>';
    }
    // Frequency options are fetched lazily on click — the backend hits Recharge
    // for the product's real allowed cadences, so we never guess an interval
    // that the selling plan would reject (or that would reprice them).
    function frequencyPanel(subId, options, current) {
      var curKey = current ? (current.unit + ':' + current.frequency) : '';
      // The label now reads "Ship every", so drop the redundant leading "Every"
      // from each option ("Every 4 months" -> "4 months").
      var opts = options.map(function (o) {
        var k = o.unit + ':' + o.frequency;
        var text = String(o.label || '').replace(/^Every\s+/i, '');
        return '<option value="' + esc(k) + '"' + (k === curKey ? ' selected' : '') + '>' + esc(text) + '</option>';
      }).join('');
      return '<div class="jr-panel"><div class="jr-freq">'
        + '<label class="jr-freq__lbl" for="freq-' + esc(subId) + '">Ship every</label>'
        + '<span class="jr-sel"><select id="freq-' + esc(subId) + '" data-freq-select>' + opts + '</select></span>'
        + '<button type="button" class="jr-act jr-act--primary" data-jr="frequency-save" data-sub="' + esc(subId) + '">Save</button>'
        + '<button type="button" class="jr-act" data-jr="panel-close" data-sub="' + esc(subId) + '">Never mind</button>'
        + '</div></div>';
    }

    function cancelPanel(subId, nextIso, interval) {
      return '<div class="jr-panel">'
        + '<p><strong>Before you cancel</strong> — want to push your next order instead? You keep your subscription and your pricing.</p>'
        + '<div class="jr-panel__actions">'
        + '<button type="button" class="jr-act jr-act--primary" data-jr="push-instead" data-sub="' + esc(subId) + '" data-next="' + esc(nextIso || '') + '" data-interval="' + esc(interval || '1 month') + '">Push my next order</button>'
        + '</div>'
        + '<label class="jr-panel__reason">Or cancel — mind sharing why?'
        + '<select>'
        + '<option value="Too expensive">Too expensive</option>'
        + '<option value="I have too much product">I have too much product</option>'
        + '<option value="Quality wasn’t for me">Quality wasn’t for me</option>'
        + '<option value="Switching products">Switching products</option>'
        + '<option value="Other">Other</option>'
        + '</select></label>'
        + '<div class="jr-panel__actions">'
        + '<button type="button" class="jr-act jr-act--danger" data-jr="cancel-confirm" data-sub="' + esc(subId) + '">Cancel my subscription</button>'
        + '<button type="button" class="jr-act" data-jr="panel-close" data-sub="' + esc(subId) + '">Never mind</button>'
        + '</div></div>';
    }

    root.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-jr]');
      if (!btn) return;
      var action = btn.getAttribute('data-jr');
      var subId = btn.getAttribute('data-sub');
      var next = btn.getAttribute('data-next');
      var interval = btn.getAttribute('data-interval');
      var panel = subId ? root.querySelector('[data-panel="' + subId + '"]') : null;

      if (action === 'skip') {
        var to = addInterval(next, interval);
        if (!window.confirm('Skip your next shipment? Your next order will move to about ' + fmtDate(to) + '.')) return;
        run(btn, { action: 'skip', subscription_id: subId },
          function (res) { var d = (res && res.next_charge_at) || to; setNext(subId, d); note(subId, '✓ Skipped — your next shipment is now ' + fmtDate(d) + '.'); });
      } else if (action === 'reschedule') {
        openPanel(subId, reschedulePanel(subId, next));
      } else if (action === 'frequency') {
        var fBtn = btn, fOrig = btn.textContent;
        btn.disabled = true; btn.textContent = 'Loading…';
        callAction({ action: 'frequency_options', subscription_id: subId })
          .then(function (res) {
            var opts = (res && res.options) || [];
            if (!opts.length) throw new Error('no options');
            openPanel(subId, frequencyPanel(subId, opts, res.current));
          })
          .catch(function (err) {
            try { console.warn('jr-account frequency_options failed', err); } catch (e) {}
            note(subId, 'Couldn’t load frequency options: ' + String((err && err.message) || 'unknown error'));
          })
          .then(function () { fBtn.disabled = false; fBtn.textContent = fOrig; });
      } else if (action === 'frequency-save') {
        var sel = panel ? panel.querySelector('[data-freq-select]') : null;
        var val = sel ? sel.value : '';
        if (!val) return;
        var bits = val.split(':');
        run(btn, { action: 'set_frequency', subscription_id: subId, unit: bits[0], frequency: parseInt(bits[1], 10) },
          function (res) {
            setInterval_(subId, (res && res.interval) || (bits[1] + ' ' + bits[0]));
            closePanel(subId);
            note(subId, '✓ ' + ((res && res.label) || 'Frequency updated') + '.');
          });
      } else if (action === 'reschedule-save') {
        var input = panel ? panel.querySelector('input[type="date"]') : null;
        var val = input ? input.value : '';
        if (!val) return;
        run(btn, { action: 'reschedule', subscription_id: subId, date: val },
          function (res) { var d = (res && res.next_charge_at) || val; setNext(subId, d); closePanel(subId); note(subId, '✓ Your next shipment is now ' + fmtDate(d) + '.'); });
      } else if (action === 'cancel') {
        // The cancellation split test (Jon, 2026-09-17): in the 'chat' arm the
        // page's Cancel hands the customer to Jasper with the request already
        // sent, and the conversation does the rest. The arm is the backend's
        // coin flip on the customer id (cancel_variant on the account read).
        if (CANCEL_VARIANT === 'chat' && document.getElementById('JrHelp')) {
          var sc = SUBS[String(subId)] || {};
          document.dispatchEvent(new CustomEvent('jr-help:ask', { detail: { text: 'I’d like to cancel my ' + (sc.product_title || sc.title || '') + ' subscription', from: 'account' } }));
          return;
        }
        // The cancellation FLOW (assets/jr-cancel-flow.js) — a body-appended
        // overlay so one component serves the page and the drawer. It is handed
        // `callAction` rather than making its own request, so there is one HTTP
        // path and one {ok:false} error contract.
        //
        // cancelPanel() is kept as the fallback for a browser where that asset
        // failed to load: a customer must always be able to cancel, even when
        // our nicer screen is unavailable.
        if (window.JRCancelFlow) {
          var s = SUBS[String(subId)] || {};
          window.JRCancelFlow.open({
            sub: {
              id: subId,
              title: s.product_title || s.title || '',
              variantTitle: s.variant_title || '',
              interval: s.interval || interval,
              nextAt: s.next_charge_at || next,
              priceText: s.price_cents != null ? money(s.price_cents) : '',
              imageUrl: s.image_url || ''
            },
            portrait: PORTRAIT_URL,
            oneTimeUrl: SHOP_URL,
            call: callAction,
            onCancelled: function () { markCancelled(subId); },
            onKept: function (offer, res) {
              // The file's existing optimistic-update contract — the page
              // renders FROM these helpers, so without this a reload would show
              // the pre-change date and read as "my change reverted".
              if (res && res.next_charge_at) setNext(subId, res.next_charge_at);
              if (res && res.interval) setInterval_(subId, res.interval);
              if (offer === 'support_handoff') note(subId, '✓ Support has your message. Someone will come back to you.');
              else if (res && res.next_charge_at) note(subId, '✓ Your next shipment is now ' + fmtDate(res.next_charge_at) + '.');
              else note(subId, '✓ Your subscription is unchanged.');
            }
          });
        } else {
          openPanel(subId, cancelPanel(subId, next, interval));
        }
      } else if (action === 'push-instead') {
        run(btn, { action: 'skip', subscription_id: subId },
          function (res) { var d = (res && res.next_charge_at) || addInterval(next, interval); setNext(subId, d); closePanel(subId); note(subId, '✓ Nice — your next order moved to ' + fmtDate(d) + '.'); });
      } else if (action === 'cancel-confirm') {
        var sel = panel ? panel.querySelector('select') : null;
        var reason = sel ? sel.value : 'Cancelled from account page';
        run(btn, { action: 'cancel', subscription_id: subId, reason: reason },
          function () { markCancelled(subId); });
      } else if (action === 'reactivate') {
        if (!window.confirm('Restart this subscription? We’ll set the next shipment and you can change the date any time.')) return;
        run(btn, { action: 'reactivate', subscription_id: subId }, function () {
          var c = card(subId);
          if (c) { c.classList.remove('jr-sub--cancelled'); var a = c.querySelector('.jr-sub__actions'); if (a) a.remove(); }
          note(subId, '✓ Restarted — reload to manage it with your other subscriptions.');
        });
      } else if (action === 'panel-close') {
        closePanel(subId);
      }
    });

    // ---- Renderers -----------------------------------------------------------
    function renderSignedOut() {
      root.innerHTML = '<div class="jr-signin"><h1>Your account</h1>'
        + '<p>Sign in to manage your subscriptions and see your details.</p>'
        + '<a class="btn" href="' + esc(LOGIN_URL) + '">Sign in</a></div>';
    }

    // Product hero image. jr-account resolves it by product ID (never a title
    // match); ~2 of 43 subscribed products have no image, so the tile falls
    // back to an empty swatch rather than a broken-image icon.
    function thumb(s, cls) {
      var url = s.image_url ? String(s.image_url).replace(/["'<>]/g, '') : '';
      var alt = esc(s.product_title || s.title || '');
      return '<div class="' + cls + '">'
        + (url ? '<img src="' + esc(url) + '" alt="' + alt + '" loading="lazy">' : '')
        + '</div>';
    }

    // ---- Buy it again --------------------------------------------------------
    // Every product this customer has ever bought. jr-account decides
    // `reorderable` — the product must still exist, be ACTIVE, and have an
    // available variant — so retired lines (Smooth 6, discontinued Shampoo /
    // Conditioner) still show as history but never offer a button that would
    // land in a dead cart.
    function buyCard(p) {
      var qty = (p.order_count > 1 ? 'Bought ' + p.order_count + ' times' : 'Bought once');
      var when = p.last_ordered_at ? ' &middot; last ' + esc(fmtDate(p.last_ordered_at)) : '';
      // Same shape as a subscription card — big image left, everything else
      // right — so a product reads the same wherever it appears.
      return '<li class="jr-buy">'
        + '<div class="jr-buy__row">'
        + '<div class="jr-buy__media">'
        + (p.image_url ? '<img src="' + esc(String(p.image_url).replace(/["'<>]/g, '')) + '" alt="' + esc(p.title || '') + '" loading="lazy">' : '')
        + '</div>'
        + '<div class="jr-buy__body">'
        + '<div class="jr-buy__title">' + esc(p.title || 'Product') + '</div>'
        + '<div class="jr-buy__meta">' + esc(qty) + when + '</div>'
        // The backend only sends buyable items now. A SKU on several live
        // products (Hand Soap's two scents) has no variant — link to the product
        // page so the customer picks, instead of choosing a scent for them.
        + '<div class="jr-buy__cta"><a class="jr-act jr-act--primary" href="'
        + (p.variant_id
            ? '/cart/add?id=' + encodeURIComponent(p.variant_id) + '&quantity=1'
            : esc(p.product_url || ('/products/' + (p.handle || ''))))
        + '">' + (p.variant_id ? 'Buy again' : 'Choose options') + '</a></div>'
        + '</div></div>'
        + '</li>';
    }

    // The flow needs the whole subscription (title, image, price, cadence), and
    // reading it back out of rendered HTML would be a second source of truth.
    var SUBS = {};
    var CANCEL_VARIANT = null;   // 'chat' | 'flow' from the account read (the split test's arm)
    function subCard(s) {
      SUBS[String(s.id)] = s;
      var id = esc(s.id);
      var interval = s.interval || '1 month';
      return '<li class="jr-sub" data-sub-card="' + id + '">'
        + '<div class="jr-sub__row">'
        + thumb(s, 'jr-sub__media')
        + '<div class="jr-sub__body">'
        + '<div class="jr-sub__title">' + esc(s.product_title || 'Subscription')
        + (s.variant_title ? ' <span class="jr-muted">' + esc(s.variant_title) + '</span>' : '') + '</div>'
        + '<div class="jr-sub__meta">'
        + (s.quantity ? '<span>Qty ' + esc(s.quantity) + '</span>' : '')
        + (s.interval ? '<span data-interval-el>Every ' + esc(s.interval) + '</span>' : '')
        + (s.price_cents != null ? '<span>' + esc(money(s.price_cents)) + '</span>' : '')
        + '</div>'
        + (s.next_charge_at ? '<div class="jr-sub__next">Next shipment: <strong data-next-el>' + esc(fmtDate(s.next_charge_at)) + '</strong></div>' : '')
        // Cancel is deliberately NOT a button: as a fourth bordered box it read
        // as an equal fourth option (Jon, 2026-08-23). It stays inside
        // .jr-sub__actions on purpose — markCancelled() removes that whole
        // block, and a stray "cancel" link under a cancelled subscription is
        // exactly the bug that splitting them out would create.
        + '<div class="jr-sub__actions">'
        + '<button type="button" class="jr-act" data-jr="skip" data-sub="' + id + '" data-next="' + esc(s.next_charge_at || '') + '" data-interval="' + esc(interval) + '">Skip a month</button>'
        + '<button type="button" class="jr-act" data-jr="reschedule" data-sub="' + id + '" data-next="' + esc(s.next_charge_at || '') + '">Change date</button>'
        + '<button type="button" class="jr-act" data-jr="frequency" data-sub="' + id + '">Change frequency</button>'
        + '<button type="button" class="jr-linkbtn" data-jr="cancel" data-sub="' + id + '" data-next="' + esc(s.next_charge_at || '') + '" data-interval="' + esc(interval) + '">Cancel subscription</button>'
        + '</div>'
        + '</div></div>'
        + '<div class="jr-sub__panel" data-panel="' + id + '" hidden></div>'
        + '<div class="jr-sub__note" data-note="' + id + '" hidden></div>'
        + '</li>';
    }

    // ---- Customer card -------------------------------------------------------
    // Who they are and how long they've been here, in ONE box: avatar, name,
    // tenure, charter badge, milestone ladder.
    //
    // There is no "Welcome back, <name>." headline any more (Jon, 2026-07-31).
    // A greeting is a thing a page says at you; the customer already knows they
    // signed in. Their name and face ARE the header, so the identity now lives
    // inside the card instead of floating in a hero above it — and the card lost
    // its ember gradient, which was decoration competing with the one thing on
    // the page that should draw the eye (the ember milestone track).
    // The name is composed SERVER-SIDE now (`data.name`, jr-account v11) so the
    // page card, the drawer card and the header avatar cannot disagree. It
    // merges the JBS profile with Shopify field by field — the live bug was a
    // profile holding first_name 'Jon' and last_name NULL rendering as "Jon"
    // while Shopify had 'Jon Lewis'. `fallbackName` stays for the window before
    // that deploys.
    function customerCard(p, l, fallbackName, resolved) {
      var name = (resolved && resolved.full)
        || [p.first_name, p.last_name].filter(Boolean).join(' ').trim()
        || p.display_name || fallbackName || '';
      var parts = name.split(/\s+/).filter(Boolean);
      var initials = (
        ((resolved && resolved.first) || p.first_name || parts[0] || '').charAt(0)
        + ((resolved && resolved.last) || p.last_name || (parts.length > 1 ? parts[parts.length - 1] : '') || '').charAt(0)
      ).toUpperCase() || '?';
      avatarUrl = p.avatar_url || null;
      avatarInitials = initials;
      // THE DISC ITSELF IS THE CONTROL — the same call jeremyboreing.com made
      // when it dropped its "Change" pill (src/pages/profile.astro): a hard
      // label bar sitting on the photo is a 19px strip across a 48px circle in
      // the drawer, which clipped its own text and covered the customer's face.
      // A button element, so it is keyboard-reachable and announced; the hover
      // ring is the affordance.
      var avatar = '<button type="button" class="jr-id__photo" data-jr-photo-open'
        + ' title="' + (avatarUrl ? 'Click to change your photo' : 'Click to add a photo') + '">'
        + avatarDisc(initials)
        + '<span class="visually-hidden">' + (avatarUrl ? 'Change your profile photo' : 'Add a profile photo') + '</span>'
        + '</button>';

      var since = '';
      if (l && l.since) {
        var d = new Date(l.since + 'T00:00:00');
        var sinceLbl = isNaN(d) ? l.since : d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
        since = 'Customer since ' + esc(sinceLbl) + (l.label ? ' &middot; ' + esc(l.label) : '');
      }

      return '<section class="jr-id">'
        + '<div class="jr-id__head">'
        + avatar
        + '<div class="jr-id__who">'
        + '<h1 class="jr-id__name">' + esc(name || 'Your account') + '</h1>'
        + (since ? '<p class="jr-id__since">' + since + '</p>' : '')
        // NO CHARTER BADGE (Jon, 2026-08-05). `loyalty.charter` is every
        // customer who joined on or before 2023-07-27 — 51% of the active base
        // (25,305 of 49,533) and 57% of all customers. It reads as a founding-
        // member distinction while being the majority, and the line directly
        // above it already says "Customer since March 2022 · 4 years", which is
        // both more specific and actually theirs. The backend still returns the
        // flag; we simply don't draw it.
        + '</div>'
        // Sign out sits WITH the identity, not in a footer under the reviews.
        // It is the one control that belongs to "who am I", and both hosts are
        // long enough (subscriptions, payment, address, buy-it-again, reviews)
        // that anything at the bottom is a scroll away — in the drawer, several.
        // Top-aligned in this row, so it reads as sitting on the avatar's line.
        + '<a class="jr-act jr-id__signout" href="' + esc(LOGOUT_URL) + '" data-jr-signout>Sign out</a>'
        + '</div>'
        // No "Remove photo" link on the card any more — it lives in the picker,
        // with the rest of the photo controls, instead of loitering under the
        // name as a permanent invitation to delete something.
        + loyaltyTrack(l)
        + '</section>';
    }

    // ---- Profile photo picker ------------------------------------------------
    // PORTED FROM jeremyboreing.com — the avatar modal in that repo's
    // src/components/Nav.astro. Click the disc, get a panel with a live preview
    // circle, a click-to-choose zone, and Cancel / Save Photo: same flow, same
    // copy ("Current photo" → "New photo — looks good?"), in this store's
    // tokens instead of JBS's copper-on-charcoal. The two sites share a
    // customer, so changing your photo should be one gesture across both.
    //
    // It replaces a hover bar that read "CHANGE" across the bottom of the disc.
    // At the drawer's 48px avatar that bar was a 19px strip over the customer's
    // own face, clipped mid-word. JBS had the identical control and deleted it
    // for the identical reason (see the comment on .jbs-profile-avatar-clickable
    // in its src/pages/profile.astro).
    //
    // WHAT DID NOT COME ACROSS: JBS uploads straight to Supabase Storage with
    // the anon key and the user's JWT inline in the page. A Shopify theme is
    // public source and holds no Supabase session, so the bytes keep taking our
    // path — jr-account mints a short-lived signed URL (avatar_upload_url), the
    // browser PUTs to it, avatar_commit records it. The server derives the
    // storage path from the App Proxy-verified customer, so nothing here
    // decides where the file lands and no key is in this file.
    //
    // WHAT WE ADDED: the dashed zone is a real drop target. On JBS it only
    // looks like one — there are no drag handlers on it — and a dashed
    // rectangle that ignores a dropped file is a promise the UI doesn't keep.
    var avatarUrl = null;
    var avatarInitials = '?';
    var photoModal = null;         // built on first open, lives on <body>
    var pendingFile = null;

    function avatarDisc(initials) {
      return '<div class="jr-id__avatar' + (avatarUrl ? ' has-img' : '') + '"'
        + (avatarUrl ? ' style="background-image:url(\'' + esc(String(avatarUrl).replace(/['"]/g, '')) + '\')"' : '')
        + '>' + esc(initials) + '</div>';
    }

    function paintAvatar() {
      var disc = el('.jr-id__avatar');
      if (disc) {
        if (avatarUrl) {
          disc.classList.add('has-img');
          disc.style.backgroundImage = "url('" + String(avatarUrl).replace(/['"]/g, '') + "')";
        } else {
          disc.classList.remove('has-img');
          disc.style.backgroundImage = '';
        }
      }
      var opener = el('[data-jr-photo-open]');
      if (opener) opener.title = avatarUrl ? 'Click to change your photo' : 'Click to add a photo';
    }

    function photoEl(sel) { return photoModal ? photoModal.querySelector(sel) : null; }

    function photoStatus(msg, kind) {
      var n = photoEl('[data-photo-status]');
      if (!n) return;
      if (!msg) { n.hidden = true; n.textContent = ''; return; }
      n.textContent = msg;
      n.className = 'jr-photo__status' + (kind ? ' is-' + kind : '');
      n.hidden = false;
    }

    function buildPhotoModal() {
      var o = document.createElement('div');
      // Carries `jr-account` as well: the design tokens are declared on that
      // class, and this panel hangs off <body>, outside the mount that has it.
      o.className = 'jr-account jr-photo';
      o.hidden = true;
      o.innerHTML = '<div class="jr-photo__box" role="dialog" aria-modal="true" aria-label="Profile photo">'
        + '<div class="jr-photo__title">Profile photo</div>'
        + '<div class="jr-photo__preview">'
        + '<div class="jr-photo__circle" data-photo-circle>'
        + '<img alt="" data-photo-img hidden>'
        + '<span class="jr-photo__initial" data-photo-initial></span>'
        + '</div>'
        + '<div class="jr-photo__label" data-photo-label>Current photo</div>'
        + '</div>'
        // A real <label> over the input, so pointer users click anywhere in the
        // zone and keyboard users get a focusable control that opens the same
        // OS picker. The input is visually hidden but NOT `hidden` — a hidden
        // input can't take focus.
        + '<label class="jr-photo__drop" data-photo-drop>'
        + '<input type="file" class="jr-photo__file" accept="image/jpeg,image/png,image/webp,image/gif" data-photo-input>'
        + '<svg class="jr-photo__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
        + '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>'
        + '</svg>'
        + '<span class="jr-photo__droptext">Click to choose a photo</span>'
        + '<span class="jr-photo__hint">JPG, PNG or WebP &middot; Max 5MB</span>'
        + '</label>'
        + '<p class="jr-photo__status" data-photo-status hidden></p>'
        + '<div class="jr-photo__actions">'
        + '<button type="button" class="jr-act" data-photo-cancel>Cancel</button>'
        + '<button type="button" class="jr-act jr-act--primary" data-photo-save disabled>Save photo</button>'
        + '</div>'
        + '<button type="button" class="jr-photo__remove" data-photo-remove hidden>Remove photo</button>'
        + '</div>';
      document.body.appendChild(o);

      // EVERY click stops at the overlay. The theme's drawer closes on any
      // click that isn't inside `.drawer` (Drawers.close, assets/theme.js), and
      // this panel has to hang off <body> — a fixed element inside the drawer
      // would position against the drawer's transform and be clipped by its
      // `overflow: clip`. Without stopping here, choosing a photo would close
      // the account behind the picker.
      o.addEventListener('click', function (e) {
        e.stopPropagation();
        if (e.target === o) return closePhoto();                       // backdrop
        if (e.target.closest('[data-photo-cancel]')) return closePhoto();
        if (e.target.closest('[data-photo-save]')) return savePhoto();
        if (e.target.closest('[data-photo-remove]')) return removePhoto();
      });

      o.addEventListener('change', function (e) {
        var input = e.target.closest('[data-photo-input]');
        if (input && input.files && input.files.length) choosePhoto(input.files[0]);
      });

      var drop = o.querySelector('[data-photo-drop]');
      ['dragenter', 'dragover'].forEach(function (t) {
        drop.addEventListener(t, function (e) { e.preventDefault(); drop.classList.add('is-over'); });
      });
      ['dragleave', 'dragend'].forEach(function (t) {
        drop.addEventListener(t, function () { drop.classList.remove('is-over'); });
      });
      drop.addEventListener('drop', function (e) {
        e.preventDefault();
        drop.classList.remove('is-over');
        var dt = e.dataTransfer;
        if (dt && dt.files && dt.files.length) choosePhoto(dt.files[0]);
      });

      return o;
    }

    // The drawer closes on Escape too (window keyup, assets/theme.js). Take the
    // key in the CAPTURE phase and stop it there, or dismissing the picker
    // would take the whole account drawer with it.
    function onPhotoKey(e) {
      if (e.keyCode !== 27) return;
      e.stopPropagation();
      closePhoto();
    }

    function openPhoto() {
      if (!photoModal) photoModal = buildPhotoModal();
      pendingFile = null;
      var input = photoEl('[data-photo-input]');
      if (input) input.value = '';
      photoEl('[data-photo-save]').disabled = true;
      photoStatus('');

      // Open on what they have now — the "here's where you're starting from"
      // state the JBS modal opens with.
      var img = photoEl('[data-photo-img]');
      var ini = photoEl('[data-photo-initial]');
      photoEl('[data-photo-circle]').classList.remove('has-new');
      photoEl('[data-photo-label]').textContent = avatarUrl ? 'Current photo' : 'No photo yet';
      if (avatarUrl) {
        img.src = avatarUrl; img.hidden = false; ini.hidden = true;
      } else {
        img.hidden = true; img.removeAttribute('src');
        ini.hidden = false; ini.textContent = avatarInitials;
      }
      photoEl('[data-photo-remove]').hidden = !avatarUrl;

      photoModal.hidden = false;
      window.addEventListener('keyup', onPhotoKey, true);
      // preventScroll for the same reason the review stars need it: focusing a
      // visually-hidden, absolutely-positioned input makes the browser scroll
      // an ancestor to "reveal" it.
      if (input) input.focus({ preventScroll: true });
    }

    function closePhoto() {
      if (!photoModal) return;
      photoModal.hidden = true;
      pendingFile = null;
      window.removeEventListener('keyup', onPhotoKey, true);
      var opener = el('[data-jr-photo-open]');
      if (opener) opener.focus();          // put focus back where it came from
    }

    function choosePhoto(file) {
      if (!file) return;
      // Checked before the round trip so an obvious mistake fails instantly.
      // The bucket enforces the real limits server-side — this is courtesy,
      // not the control.
      if (file.size > 5 * 1024 * 1024) {
        return photoStatus('That photo is over 5MB. Please pick a smaller one.', 'err');
      }
      if (!/^image\//.test(file.type)) {
        return photoStatus('That doesn’t look like an image file.', 'err');
      }
      pendingFile = file;
      photoStatus('');

      var reader = new FileReader();
      reader.onload = function (ev) {
        var img = photoEl('[data-photo-img]');
        if (!img) return;
        img.src = ev.target.result;
        img.hidden = false;
        photoEl('[data-photo-initial]').hidden = true;
        photoEl('[data-photo-circle]').classList.add('has-new');
        photoEl('[data-photo-label]').textContent = 'New photo — looks good?';
        photoEl('[data-photo-save]').disabled = false;
      };
      reader.onerror = function () { photoStatus('We couldn’t read that file.', 'err'); };
      reader.readAsDataURL(file);
    }

    function savePhoto() {
      if (!pendingFile) return;
      var file = pendingFile;
      var save = photoEl('[data-photo-save]');
      save.disabled = true;
      photoStatus('Uploading…', 'busy');

      callAction({ action: 'avatar_upload_url', content_type: file.type })
        .then(function (res) {
          return fetch(res.upload_url, {
            method: 'PUT',
            headers: { 'Content-Type': file.type },
            body: file
          }).then(function (r) {
            if (!r.ok) throw new Error('Upload failed (' + r.status + ')');
            return callAction({ action: 'avatar_commit', path: res.path });
          });
        })
        .then(function (res) {
          avatarUrl = res.avatar_url || null;
          paintAvatar();
          forgetHeaderIdentity();
          photoStatus('Photo saved.', 'ok');
          // Let them see it land before the panel goes, same beat as JBS.
          window.setTimeout(closePhoto, 1200);
        })
        .catch(function (err) {
          photoStatus(String((err && err.message) || 'We couldn’t save that photo.'), 'err');
          save.disabled = false;
        });
    }

    function removePhoto() {
      if (!window.confirm('Remove your profile photo?')) return;
      var btn = photoEl('[data-photo-remove]');
      btn.disabled = true;
      photoStatus('Removing…', 'busy');
      callAction({ action: 'avatar_remove' })
        .then(function () {
          avatarUrl = null;
          paintAvatar();
          forgetHeaderIdentity();
          photoStatus('Photo removed.', 'ok');
          window.setTimeout(closePhoto, 1200);
        })
        .catch(function (err) {
          photoStatus(String((err && err.message) || 'We couldn’t remove that photo.'), 'err');
        })
        .then(function () { btn.disabled = false; });
    }

    // The header caches the name/photo in sessionStorage for ten minutes
    // (snippets/header-icons.liquid). Drop it whenever the face changes, so the
    // nav catches up on the next page instead of showing the old one.
    // Prefix sweep, not one key: header-icons.liquid keys the cache per customer
    // (`jr-header-identity:<id>`), and this file has no Liquid to know which id
    // it is running under.
    function forgetHeaderIdentity() {
      try {
        var ss = window.sessionStorage;
        for (var i = ss.length - 1; i >= 0; i--) {
          var k = ss.key(i);
          if (k && k.indexOf('jr-header-identity') === 0) ss.removeItem(k);
        }
      } catch (e) {}
    }

    root.addEventListener('click', function (e) {
      if (!e.target.closest('[data-jr-photo-open]')) return;
      openPhoto();
    });

    // Signing out drops that same cache on the way out — signing out does not
    // clear sessionStorage, so without this the nav would keep showing the
    // previous customer's FACE after they left. A small thing on your own
    // laptop and not a small thing on a shared one. Synchronous, and we don't
    // preventDefault: the link navigates as normal.
    root.addEventListener('click', function (e) {
      if (!e.target.closest('[data-jr-signout]')) return;
      forgetHeaderIdentity();
    });

    // ---- Loyalty ladder ------------------------------------------------------
    // The backend computes tenure + which rungs are reached/earnable; this only
    // draws it. Nodes shown = EVERY year they have completed, plus exactly ONE
    // rung ahead (Jon, 2026-08-23). The ladder is a record first and a target
    // second: a four-year customer should see four filled dots behind them, not
    // a single dot and three empty years they haven't lived yet. So "today"
    // sits inside the LAST segment, not the first.
    //
    // The sub-year "3 months" rung drops off once a year rung is behind them —
    // it is a milestone for a new customer and clutter for an old one. Someone
    // still inside their first year keeps it, and someone inside their first
    // three months gets "Joined" so the bar is never a single dot.
    function loyaltyTrack(l) {
      if (!l) return '';
      var rungs = l.milestones || [];
      var next = l.next;
      var isYear = function (m) { return /^y/.test(m.key || ''); };
      var done = rungs.filter(function (m) { return m.reached; });
      if (done.some(isYear)) done = done.filter(isYear);

      var nodes = done.map(function (m) { return { label: m.label, state: 'is-done' }; });
      if (!nodes.length) nodes.push({ label: 'Joined', state: 'is-done' });
      if (next) nodes.push({ label: next.label, state: 'is-next' });
      if (nodes.length < 2) nodes.push({ label: 'Thank you', state: 'is-done' });

      // Every segment but the last is behind them; the last one is however far
      // into the current year they are.
      var segs = nodes.length - 1;
      var pct = next
        ? Math.max(0, Math.min(100, ((segs - 1) + (Number(next.progress_pct) || 0) / 100) / segs * 100))
        : 100;

      // Past six rungs the uppercase labels collide even at full page width, so
      // the ladder keeps only the ends and the one they're walking toward. (The
      // drawer hides every label already — see .jr-account--compact.)
      var dense = nodes.length > 6 ? ' jr-track--dense' : '';

      return '<div class="jr-track' + dense + '">'
        + '<span class="jr-track__line"></span>'
        + '<span class="jr-track__fill" style="width:calc(' + pct.toFixed(1) + '% - 14px)"></span>'
        + '<ul class="jr-track__nodes">'
        + nodes.map(function (n) {
            return '<li class="' + n.state + '"><span class="jr-track__dot"></span>'
              + '<span class="jr-track__lbl">' + esc(n.label) + '</span></li>';
          }).join('')
        + '</ul></div>'
        + (next
            ? '<p class="jr-id__next">' + esc(next.days_away) + ' days to your ' + esc(next.label) + ' milestone.</p>'
            : '<p class="jr-id__next">Thank you for shaving with us.</p>');
    }

    function pastCard(s) {
      var id = esc(s.id);
      return '<li class="jr-sub jr-sub--cancelled" data-sub-card="' + id + '">'
        + '<div class="jr-sub__row">'
        + thumb(s, 'jr-sub__media')
        + '<div class="jr-sub__body">'
        + '<div class="jr-sub__title">' + esc(s.product_title || 'Subscription')
        + (s.variant_title ? ' <span class="jr-muted">' + esc(s.variant_title) + '</span>' : '') + '</div>'
        + '<div class="jr-sub__meta">'
        + (s.interval ? '<span>Every ' + esc(s.interval) + '</span>' : '')
        + (s.price_cents != null ? '<span>' + esc(money(s.price_cents)) + '</span>' : '')
        + (s.cancelled_at ? '<span>Cancelled ' + esc(fmtDate(String(s.cancelled_at).slice(0, 10))) + '</span>' : '')
        + '</div>'
        + '<div class="jr-sub__actions">'
        + '<button type="button" class="jr-act jr-act--primary" data-jr="reactivate" data-sub="' + id + '">Start again</button>'
        + '</div>'
        + '</div></div>'
        + '<div class="jr-sub__note" data-note="' + id + '" hidden></div>'
        + '</li>';
    }

    function render(data) {
      CANCEL_VARIANT = (data && (data.cancel_variant === 'chat' || data.cancel_variant === 'flow')) ? data.cancel_variant : null;
      var active = (data.subscriptions || []).filter(function (s) { return s.status === 'active'; });
      var past = (data.subscriptions || []).filter(function (s) { return s.status !== 'active'; });

      var html = customerCard(data.profile || {}, data.loyalty, data.customer_name, data.name);

      // Payment method: rendered as a placeholder and hydrated after paint.
      // Built here, placed below — normally after the subscriptions, but FIRST
      // when the page was opened with ?focus=payment.
      //
      // WHY (Jon, 2026-09-26): the card-expiry email is a link test. Its control
      // arm lands on the normal page, where the card fix sits under the
      // subscription cards and their cancel buttons — 13 of the first 22
      // post-email cancellations clicked through and cancelled within minutes.
      // The other arm lands here with the one thing the email is about at the
      // top. Nothing is removed, only reordered: every control is still on the
      // page for anyone who scrolls.
      var paySec = active.length
        ? '<section class="jr-sec" id="payment"><div class="jr-sec__head"><h2>Payment method</h2></div>'
          + '<div data-jr-pay><p class="jr-muted">Loading your payment method&hellip;</p></div></section>'
        : '';
      var payFirst = opts.focus === 'payment';
      if (payFirst) html += paySec;

      html += '<section class="jr-sec" id="subscriptions"><div class="jr-sec__head"><h2>Your subscriptions</h2></div>';
      if (active.length) {
        html += '<ul class="jr-subs">' + active.map(subCard).join('') + '</ul>';
      } else {
        html += '<p class="jr-muted">You don’t have an active subscription right now.</p>'
          + '<p><a class="btn btn--small" href="/collections/all">Shop razors</a></p>';
      }
      html += '</section>';

      // Previous subscriptions sit directly under the current ones — they're the
      // most likely thing a returning customer wants, and each is one click from
      // coming back. Expanded, not buried in a disclosure.
      if (past.length) {
        html += '<section class="jr-sec"><div class="jr-sec__head"><h2>Previous subscriptions</h2></div>'
          + '<ul class="jr-subs">' + past.map(pastCard).join('') + '</ul></section>';
      }

      // Shipping address: rendered as a placeholder and hydrated after paint.
      // We don't sync Recharge addresses, so this is a LIVE call — deliberately
      // kept off the critical path so the page never waits on Recharge to draw.
      if (!payFirst) html += paySec;

      if (active.length) {
        html += '<section class="jr-sec" id="jr-addr-sec"><div class="jr-sec__head"><h2>Shipping address</h2></div>'
          + '<div data-jr-addr><p class="jr-muted">Loading your address&hellip;</p></div></section>';
      }

      var buys = data.purchases || [];
      if (buys.length) {
        html += '<section class="jr-sec" id="buy-again"><div class="jr-sec__head"><h2>Buy it again</h2>'
          + '<span class="jr-muted">' + buys.length + ' product' + (buys.length === 1 ? '' : 's') + ' you’ve ordered</span></div>'
          + '<ul class="jr-buys">' + buys.map(buyCard).join('') + '</ul></section>';

        // Reviews hydrate after paint, like the address and payment panels: the
        // backend re-derives the purchase set (it is the authorization boundary
        // for a write) and reads their existing reviews, and none of that should
        // hold up the account drawing.
        html += '<section class="jr-sec" id="reviews"><div class="jr-sec__head"><h2>Your reviews</h2>'
          + '<span class="jr-muted" data-jr-rev-count></span></div>'
          + '<div data-jr-rev><p class="jr-muted">Loading your reviews&hellip;</p></div></section>';
      }


      root.innerHTML = html;
      if (el('[data-jr-addr]')) loadAddresses();
      if (el('[data-jr-pay]')) loadPayment();
      if (el('[data-jr-rev]')) loadReviews();
      scrollToHash();
    }

    // Page host only. Email, support macros and old links point at
    // /pages/my-account#payment and friends. The browser resolves a hash at
    // load, while the page is still an empty shell, so nothing would happen —
    // the jump has to be ours, once the sections exist.
    function scrollToHash() {
      if (!opts.scrollToHash) return;      // meaningless inside a drawer
      var id = (window.location.hash || '').slice(1);
      if (!id) return;
      var target = root.querySelector('#' + CSS.escape(id));
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    // ---- Reviews -------------------------------------------------------------
    // Reviews live in Judge.me. The backend serves the customer's own reviews
    // from the mirror we already sync for the console, and writes go through
    // /apps/jr/ to Judge.me — so this page never holds a Judge.me credential and
    // the customer never has to find the review widget on a product page.
    var revCache = {};
    // Reads work off our mirror even when Judge.me isn't reachable/configured;
    // writes don't. The backend tells us which, so we never show a button that
    // is guaranteed to fail.
    var revCanWrite = true;

    function starsRO(n) {
      var full = Math.max(0, Math.min(5, Math.round(Number(n) || 0)));
      var out = '';
      for (var i = 1; i <= 5; i++) out += (i <= full ? '★' : '<span>★</span>');
      return '<span class="jr-rated__stars" role="img" aria-label="' + full + ' out of 5 stars">' + out + '</span>';
    }

    // Radios run 5→1 in the DOM so the CSS can fill every star left of the one
    // chosen (see .jr-stars). row-reverse puts 1 back on the left visually.
    function starsInput(pid, current) {
      var out = '';
      for (var v = 5; v >= 1; v--) {
        var id = 'jr-star-' + pid + '-' + v;
        out += '<input type="radio" name="jr-star-' + esc(pid) + '" id="' + esc(id) + '" value="' + v + '"'
          + (Number(current) === v ? ' checked' : '') + '>'
          + '<label for="' + esc(id) + '" title="' + v + ' star' + (v === 1 ? '' : 's') + '">'
          + '<span class="visually-hidden">' + v + ' star' + (v === 1 ? '' : 's') + '</span>★</label>';
      }
      return '<div class="jr-stars">' + out + '</div>';
    }

    function revForm(p) {
      var r = p.review || {};
      var pid = String(p.product_id);
      return '<div class="jr-panel">'
        + starsInput(pid, r.rating)
        + '<label class="jr-rev__f">Headline <span class="jr-muted">(optional)</span>'
        + '<input type="text" maxlength="150" data-rev-title value="' + esc(r.title || '') + '"'
        + ' placeholder="Sum it up in a few words"></label>'
        + '<label class="jr-rev__f">Your review'
        + '<textarea maxlength="5000" data-rev-body placeholder="How has it worked for you?">' + esc(r.body || '') + '</textarea></label>'
        + '<div class="jr-panel__actions">'
        + '<button type="button" class="jr-act jr-act--primary" data-jr="rev-save" data-pid="' + esc(pid) + '">'
        + (r.rating ? 'Update review' : 'Post review') + '</button>'
        + '<button type="button" class="jr-act" data-jr="rev-cancel" data-pid="' + esc(pid) + '">Never mind</button>'
        + '</div><p class="jr-rev__err" data-rev-err hidden></p></div>';
    }

    function revCard(p) {
      var pid = esc(String(p.product_id));
      var r = p.review;
      var titleHtml = p.product_url
        ? '<a href="' + esc(p.product_url) + '">' + esc(p.title || 'Product') + '</a>'
        : esc(p.title || 'Product');

      var body = '';
      if (r) {
        body = '<div class="jr-rated">' + starsRO(r.rating)
          + '<span class="jr-rev__meta">Reviewed' + (r.created_at ? ' ' + esc(fmtDate(String(r.created_at).slice(0, 10))) : '') + '</span></div>'
          + '<p class="jr-rev__quote">' + (r.title ? '<strong>' + esc(r.title) + '</strong>' : '') + esc(r.body || '') + '</p>'
          // Say so plainly rather than let them wonder why it isn't on the
          // product page yet — Judge.me publishes on its own schedule.
          + (r.pending ? '<p class="jr-rev__pending">Posted — it’ll appear on the product page once it’s published.</p>' : '');
      } else {
        body = '<p class="jr-rev__meta">You haven’t reviewed this yet.</p>';
      }

      return '<li class="jr-rev" data-rev-card="' + pid + '">'
        + '<div class="jr-rev__row">'
        + '<div class="jr-rev__media">'
        + (p.image_url ? '<img src="' + esc(String(p.image_url).replace(/["'<>]/g, '')) + '" alt="' + esc(p.title || '') + '" loading="lazy">' : '')
        + '</div>'
        + '<div class="jr-rev__body">'
        + '<div class="jr-rev__title">' + titleHtml + '</div>'
        + body
        + (revCanWrite
            ? '<div class="jr-rev__actions">'
              + '<button type="button" class="jr-act' + (r ? '' : ' jr-act--primary') + '" data-jr="rev-edit" data-pid="' + pid + '">'
              + (r ? 'Edit review' : 'Write a review') + '</button>'
              + '</div>'
            : '')
        + '</div></div>'
        + '<div data-rev-panel="' + pid + '" hidden></div>'
        + '<div class="jr-rev__note" data-rev-note="' + pid + '" hidden></div>'
        + '</li>';
    }

    function renderReviews(products) {
      var host = el('[data-jr-rev]');
      if (!host) return;
      revCache = {};
      products.forEach(function (p) { revCache[String(p.product_id)] = p; });

      var done = products.filter(function (p) { return p.review; }).length;
      var count = el('[data-jr-rev-count]');
      if (count) count.textContent = products.length ? done + ' of ' + products.length + ' reviewed' : '';

      host.innerHTML = products.length
        ? '<ul class="jr-revs">' + products.map(revCard).join('') + '</ul>'
        : '<p class="jr-muted">Nothing to review yet.</p>';
    }

    function loadReviews() {
      callAction({ action: 'reviews' })
        .then(function (res) {
          if (res && res.can_write === false) revCanWrite = false;
          renderReviews((res && res.products) || []);
        })
        .catch(function (err) {
          try { console.warn('jr-account reviews load failed', err); } catch (e) {}
          var host = el('[data-jr-rev]');
          if (!host) return;
          host.innerHTML = '<p class="jr-muted">We couldn’t load your reviews just now.</p>'
            + '<p class="jr-addr__why">' + esc(String((err && err.message) || 'unknown error')) + '</p>';
        });
    }

    root.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-jr^="rev-"]');
      if (!btn) return;
      var pid = btn.getAttribute('data-pid');
      var card = root.querySelector('[data-rev-card="' + pid + '"]');
      var pane = card && card.querySelector('[data-rev-panel="' + pid + '"]');
      var act = btn.getAttribute('data-jr');
      if (!pane) return;

      if (act === 'rev-edit') {
        pane.innerHTML = revForm(revCache[pid] || { product_id: pid });
        pane.hidden = false;
        var firstStar = pane.querySelector('.jr-stars input:checked') || pane.querySelector('.jr-stars input[value="5"]');
        // preventScroll IS LOAD-BEARING, not a nicety. The star radios are
        // visually hidden and absolutely positioned, so focusing one makes the
        // browser scroll its nearest scrollable ancestor to "reveal" it. Inside
        // the account drawer that ancestor is `.drawer`, which is
        // overflow:hidden — it scrolls programmatically but renders no
        // scrollbar, so the whole panel slid ~600px out of view and the drawer
        // went blank with no way for the customer to scroll it back.
        // Measured on the live theme, 2026-08-04: .drawer__contents at top:-599.
        if (firstStar) firstStar.focus({ preventScroll: true });
      } else if (act === 'rev-cancel') {
        pane.hidden = true; pane.innerHTML = '';
      } else if (act === 'rev-save') {
        var checked = pane.querySelector('.jr-stars input:checked');
        var errEl = pane.querySelector('[data-rev-err]');
        var show = function (msg) { if (errEl) { errEl.textContent = msg; errEl.hidden = false; } };
        if (errEl) errEl.hidden = true;
        if (!checked) return show('Please choose a star rating.');
        var text = (pane.querySelector('[data-rev-body]') || {}).value || '';
        if (!text.trim()) return show('Please write a few words about the product.');

        var orig = btn.textContent;
        btn.disabled = true; btn.textContent = 'Saving…';
        callAction({
          action: 'save_review',
          product_id: pid,
          rating: Number(checked.value),
          title: ((pane.querySelector('[data-rev-title]') || {}).value || '').trim(),
          body: text.trim()
        }).then(function (res) {
          // Re-render just this card from the returned review, so the customer
          // sees exactly what was saved without reloading the whole account.
          var p = revCache[pid] || { product_id: pid };
          p.review = res.review;
          revCache[pid] = p;
          // Parse in a <ul>, not a bare div: an <li> assigned to a non-list
          // parent is legal here but a <tbody>/<table> parent would foster it
          // out of the fragment entirely.
          var fresh = document.createElement('ul');
          fresh.innerHTML = revCard(p);
          if (card && fresh.firstElementChild) card.replaceWith(fresh.firstElementChild);
          var note = root.querySelector('[data-rev-note="' + pid + '"]');
          if (note) {
            note.textContent = res.updated ? '✓ Your review has been updated.' : '✓ Thanks — your review has been posted.';
            note.hidden = false;
          }
          var count = el('[data-jr-rev-count]');
          if (count) {
            var all = Object.keys(revCache).map(function (k) { return revCache[k]; });
            count.textContent = all.filter(function (x) { return x.review; }).length + ' of ' + all.length + ' reviewed';
          }
        }).catch(function (err) {
          show(String((err && err.message) || 'We couldn’t save that just now.'));
          btn.disabled = false; btn.textContent = orig;
        });
      }
    });

    // ---- Payment method ------------------------------------------------------
    // Card data NEVER reaches us. Updating hands off to Recharge's own hosted
    // page via a temporary authenticated URL, so the customer isn't asked to log
    // in again. The expiry warning is the point: "my card expired" is ~2,000
    // support tickets, and it is entirely preventable by saying so BEFORE the
    // renewal fails.
    function payCard(m) {
      var brand = m.brand ? String(m.brand) : 'Card';
      var exp = (m.exp_month && m.exp_year)
        ? String(m.exp_month).padStart(2, '0') + '/' + String(m.exp_year).slice(-2) : '';
      return '<div class="jr-pay__card">'
        + '<span class="jr-pay__brand">' + esc(brand) + (m.last4 ? ' ending ' + esc(m.last4) : '') + '</span>'
        + (exp ? '<span class="jr-pay__exp">Expires ' + esc(exp) + '</span>' : '')
        + '</div>'
        + (m.expired
            ? '<p class="jr-pay__warn">This card has expired — your next order will fail until it’s updated.</p>'
            : m.expiring_soon
              ? '<p class="jr-pay__warn jr-pay__soon">This card expires soon. Updating it now avoids a missed shipment.</p>'
              : '');
    }

    function renderPayment(res) {
      var host = el('[data-jr-pay]');
      if (!host) return;
      var methods = (res && res.payment_methods) || [];
      // Only the card(s) that actually bill — the backend filters to those. No
      // note about other saved cards: they're not this page's business.
      var body = methods.length
        ? methods.map(payCard).join('')
        : '<p class="jr-muted">No payment method on file.</p>';
      // A BUTTON, NOT A LINK — the URL is fetched when they click it. See the
      // handler below for why an href baked in at render time is a bug.
      host.innerHTML = '<div class="jr-pay">' + body
        + '<div class="jr-panel__actions">'
        + '<button type="button" class="jr-act" data-jr-pay-open>Update payment method</button>'
        + '</div>'
        + '</div>';
    }

    // MINTED ON CLICK, NEVER AT RENDER (Jon, 2026-08-05).
    //
    // Recharge portal URLs are /tools/recurring/portal/<hash>/<section>?token=…
    // and THE TOKEN IS ONE-TIME: it authenticates one visit, after which
    // Recharge carries the session by cookie. A URL written into an href when
    // the account renders therefore works exactly once. Open the account,
    // update your card, come back without reloading the page, click again —
    // the token is spent, and instead of the card screen Recharge drops the
    // customer on its own login/overview page. Same outcome if the panel sits
    // open long enough for the token to age out before the first click.
    //
    // Fetching per click also means we stop asking Recharge to mint a portal
    // URL for every customer who merely opens their account and never touches
    // this button.
    root.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-jr-pay-open]');
      if (!btn) return;
      btn.disabled = true;
      btn.textContent = 'Opening…';
      callAction({ action: 'payment' })
        .then(function (res) {
          // Straight to the card screen. Recharge's portal can't be embedded
          // (x-frame-options: DENY, frame-ancestors 'none') and card entry has
          // to happen on their page, so the least we can do is skip the
          // overview and the Account menu they'd otherwise hunt through.
          //
          // payment_url first for when the backend can mint a real one again;
          // the redirect URL is the floor, and today it is what actually runs.
          // portal_url is deliberately NOT in this chain any more: it points at
          // the overview, which is the screen we are trying to skip.
          window.location.href = (res && res.payment_url) || PORTAL_CARD_URL;
        })
        .catch(function (err) {
          try { console.warn('jr-account payment url failed', err); } catch (e2) {}
          window.location.href = PORTAL_CARD_URL;
        });
    });

    function loadPayment() {
      callAction({ action: 'payment' })
        .then(renderPayment)
        .catch(function (err) {
          try { console.warn('jr-account payment load failed', err); } catch (e) {}
          var host = el('[data-jr-pay]');
          if (!host) return;
          host.innerHTML = '<div class="jr-pay"><p class="jr-muted">We couldn’t load your payment method just now.</p>'
            + '<div class="jr-panel__actions"><a class="jr-act" href="' + esc(PORTAL_CARD_URL) + '">Update payment method</a></div>'
            + '<p class="jr-addr__why">' + esc(String((err && err.message) || 'unknown error')) + '</p></div>';
        });
    }

    // ---- Shipping address ----------------------------------------------------
    var ADDR_FIELDS = [
      ['first_name', 'First name'], ['last_name', 'Last name'], ['company', 'Company'],
      ['address1', 'Address'], ['address2', 'Apt / suite'], ['city', 'City'],
      ['province', 'State / province'], ['zip', 'ZIP / postal code'],
      ['country_code', 'Country code'], ['phone', 'Phone']
    ];

    function addrLines(a) {
      var name = [a.first_name, a.last_name].filter(Boolean).join(' ');
      var region = [a.city, a.province].filter(Boolean).join(', ');
      // Country joins the city/state/ZIP line — on its own row it read as an
      // orphaned fragment.
      var locality = [region, a.zip, a.country_code].filter(Boolean).join(' ');
      return [name, a.company, a.address1, a.address2, locality, a.phone]
        .filter(Boolean).map(esc).join('<br>');
    }

    function addrCard(a) {
      // Say WHAT SHIPS HERE. In Recharge an address owns subscriptions, so a
      // customer can hold several identical-looking addresses; without this label
      // there is no way to tell which card governs which shipment.
      var ships = (a.ships || []);
      return '<div class="jr-addr" data-addr="' + esc(a.id) + '">'
        + (ships.length ? '<p class="jr-addr__ships">Ships: ' + esc(ships.join(', ')) + '</p>' : '')
        + '<p class="jr-addr__lines">' + addrLines(a) + '</p>'
        + '<div class="jr-panel__actions">'
        + '<button type="button" class="jr-act" data-jr="addr-edit" data-addr="' + esc(a.id) + '">Edit address</button>'
        + '</div><div class="jr-addr__panel" hidden></div></div>';
    }

    function addrForm(a) {
      return '<div class="jr-panel">'
        + ADDR_FIELDS.map(function (f) {
            var req = (f[0] === 'address1' || f[0] === 'city' || f[0] === 'zip');
            return '<label class="jr-addr__f">' + esc(f[1]) + (req ? ' *' : '')
              + '<input type="text" data-f="' + f[0] + '" value="' + esc(a[f[0]] || '') + '"'
              + (f[0] === 'country_code' ? ' maxlength="2"' : '') + '></label>';
          }).join('')
        + '<div class="jr-panel__actions">'
        + '<button type="button" class="jr-act jr-act--primary" data-jr="addr-save" data-addr="' + esc(a.id) + '">Save address</button>'
        + '<button type="button" class="jr-act" data-jr="addr-cancel" data-addr="' + esc(a.id) + '">Never mind</button>'
        + '</div><p class="jr-addr__note" data-addr-note hidden></p></div>';
    }

    var addrCache = {};
    function renderAddresses(list, inactive) {
      inactive = inactive || 0;
      var host = el('[data-jr-addr]');
      if (!host) return;
      addrCache = {};
      list.forEach(function (a) { addrCache[String(a.id)] = a; });
      host.innerHTML = (list.length
        ? list.map(addrCard).join('')
        : '<p class="jr-muted">No shipping address on file yet.</p>')
        ;   // No "N hidden" notice — don't tell people about data we chose not to show.
    }

    function loadAddresses() {
      callAction({ action: 'address' })
        .then(function (res) { renderAddresses((res && res.addresses) || [], res && res.inactive_count); })
        .catch(function (err) {
          try { console.warn('jr-account address load failed', err); } catch (e) {}
          var host = el('[data-jr-addr]');
          if (!host) return;
          // SHOW THE REAL REASON. A generic "couldn't load" told us nothing and
          // cost a debugging round trip; a Recharge scope/permission failure is
          // actionable the moment it's legible. Kept muted + still offering the
          // hosted portal so a customer always has a way forward.
          var why = String((err && err.message) || 'unknown error');
          host.innerHTML = '<p class="jr-muted">We couldn’t load your address just now. '
            + '<a href="' + esc(HOSTED_PORTAL) + '">Manage it in the subscription portal</a>.</p>'
            + '<p class="jr-addr__why">' + esc(why) + '</p>';
        });
    }

    root.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-jr^="addr-"]');
      if (!btn) return;
      var id = btn.getAttribute('data-addr');
      var wrapEl = root.querySelector('.jr-addr[data-addr="' + id + '"]');
      var pane = wrapEl && wrapEl.querySelector('.jr-addr__panel');
      var act = btn.getAttribute('data-jr');

      if (act === 'addr-edit') {
        if (!pane || !addrCache[id]) return;
        pane.innerHTML = addrForm(addrCache[id]); pane.hidden = false;
      } else if (act === 'addr-cancel') {
        if (pane) { pane.hidden = true; pane.innerHTML = ''; }
      } else if (act === 'addr-save') {
        var payload = { action: 'set_address', address_id: id };
        var inputs = pane ? pane.querySelectorAll('[data-f]') : [];
        for (var i = 0; i < inputs.length; i++) payload[inputs[i].getAttribute('data-f')] = inputs[i].value.trim();
        var noteEl = pane && pane.querySelector('[data-addr-note]');
        var orig = btn.textContent;
        btn.disabled = true; btn.textContent = 'Saving…';
        callAction(payload)
          .then(function (res) { renderAddresses((res && res.addresses) || [], res && res.inactive_count); })
          .catch(function (err) {
            // Surface the real reason here (required field, Recharge rejection) —
            // an address form that fails silently is worse than no form.
            if (noteEl) { noteEl.textContent = String((err && err.message) || 'Could not save that address.'); noteEl.hidden = false; }
            btn.disabled = false; btn.textContent = orig;
          });
      }
    });

    // ---- Load ----------------------------------------------------------------
    fetch('/apps/jr/', { headers: { accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.json().then(function (j) { return { status: r.status, body: j }; }); })
      .then(function (res) {
        if (res.status === 401 || (res.body && res.body.error === 'not logged in')) { renderSignedOut(); return; }
        if (res.status >= 400) throw new Error(res.body && res.body.error || 'error');
        render(res.body);
      })
      .catch(function () {
        root.innerHTML = '<p class="jr-muted">Your account is momentarily unavailable. '
          + '<a href="' + esc(HOSTED_PORTAL) + '" class="text-link">Open the subscription portal</a></p>';
      });

  }

  return { mount: mount };
})();
